import type { ClassSummary, StaffSummary } from '@school-erp/types';
import { createPeriodSchema, createTimetableEntrySchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  HiddenFieldErrors,
  Notice,
  SchoolSelect,
  errorMessage,
  primaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { TimetableGrid, type Lesson, type Period } from './TimetableGrid';

interface Subject {
  id: string;
  name: string;
}

const issuesToErrors = (issues: { path: PropertyKey[]; message: string }[]) =>
  Object.fromEntries(issues.map((i) => [String(i.path[0]), i.message]));

export function TimetablePage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [schoolId, setSchoolId] = useState('');
  const [mode, setMode] = useState<'section' | 'teacher'>('section');
  const [sectionId, setSectionId] = useState('');
  const [staffId, setStaffId] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const manage = !!schoolId && can('timetable.manage', schoolId);

  const classes = useQuery({
    queryKey: ['classes', schoolId],
    queryFn: () => api<ClassSummary[]>(`/schools/${schoolId}/classes`),
    enabled: !!schoolId,
  });
  const sections = (classes.data ?? []).flatMap((c) =>
    c.sections.map((s) => ({ id: s.id, label: `${c.name} – ${s.name}` })),
  );
  const staff = useQuery({
    queryKey: ['staff', schoolId, 'teaching'],
    queryFn: () =>
      apiList<StaffSummary>('/staff', {
        query: { schoolId, staffType: 'teaching', pageSize: 100 },
      }).then((r) => r.data),
    enabled: !!schoolId && (mode === 'teacher' || manage),
  });
  const subjects = useQuery({
    queryKey: ['subjects', schoolId],
    queryFn: () => api<Subject[]>(`/schools/${schoolId}/subjects`),
    enabled: manage,
  });
  const periods = useQuery({
    queryKey: ['periods', schoolId],
    queryFn: () => api<Period[]>(`/schools/${schoolId}/periods`),
    enabled: !!schoolId,
  });

  const target = mode === 'section' ? sectionId : staffId;
  const lessons = useQuery({
    queryKey: ['timetable', schoolId, mode, target],
    queryFn: () =>
      api<Lesson[]>(`/schools/${schoolId}/timetable-entries`, {
        query: mode === 'section' ? { sectionId: target } : { staffId: target },
      }),
    enabled: !!schoolId && !!target,
  });

  const refresh = () => {
    setMessage(null);
    void queryClient.invalidateQueries({ queryKey: ['timetable', schoolId] });
    void queryClient.invalidateQueries({ queryKey: ['periods', schoolId] });
  };
  const fail = (e: unknown) => setMessage(errorMessage(e));
  const removeLesson = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/timetable-entries/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: fail,
  });
  const removePeriod = useMutation({
    mutationFn: (id: string) => api(`/schools/${schoolId}/periods/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: fail,
  });

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <h1 className="text-2xl font-bold">{t('tt.title')}</h1>
      <SchoolSelect
        value={schoolId}
        onChange={(id) => {
          setSchoolId(id);
          setSectionId('');
          setStaffId('');
        }}
      />
      {message && <Notice kind="error">{message}</Notice>}

      {schoolId && (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label htmlFor="tt-mode" className="block text-sm font-medium">
                {t('tt.title')}
              </label>
              <select
                id="tt-mode"
                value={mode}
                onChange={(e) => setMode(e.target.value as 'section' | 'teacher')}
                className={selectClass}
              >
                <option value="section">{t('tt.viewBySection')}</option>
                <option value="teacher">{t('tt.viewByTeacher')}</option>
              </select>
            </div>
            {mode === 'section' ? (
              <div>
                <label htmlFor="tt-section" className="block text-sm font-medium">
                  {t('tt.section')}
                </label>
                <select
                  id="tt-section"
                  value={sectionId}
                  onChange={(e) => setSectionId(e.target.value)}
                  className={selectClass}
                >
                  <option value="">{t('tt.choose')}</option>
                  {sections.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label htmlFor="tt-teacher" className="block text-sm font-medium">
                  {t('tt.teacher')}
                </label>
                <select
                  id="tt-teacher"
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                  className={selectClass}
                >
                  <option value="">{t('tt.choose')}</option>
                  {staff.data?.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.firstName} {s.lastName}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {target && lessons.data && (
            <>
              {lessons.data.length === 0 && <p className="text-slate-600">{t('tt.empty')}</p>}
              <TimetableGrid
                periods={periods.data ?? []}
                lessons={lessons.data}
                show={mode === 'section' ? 'teacher' : 'section'}
                onRemove={manage ? (l) => removeLesson.mutate(l.id) : undefined}
              />
            </>
          )}

          {manage && (
            <>
              {mode === 'section' && sectionId && (
                <LessonForm
                  schoolId={schoolId}
                  sectionId={sectionId}
                  periods={(periods.data ?? []).filter((p) => !p.isBreak)}
                  subjects={subjects.data ?? []}
                  staff={staff.data ?? []}
                  onDone={refresh}
                  onError={fail}
                />
              )}
              <PeriodsPanel
                schoolId={schoolId}
                periods={periods.data ?? []}
                onDone={refresh}
                onError={fail}
                onDelete={(id) => removePeriod.mutate(id)}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

function LessonForm({
  schoolId,
  sectionId,
  periods,
  subjects,
  staff,
  onDone,
  onError,
}: {
  schoolId: string;
  sectionId: string;
  periods: Period[];
  subjects: Subject[];
  staff: StaffSummary[];
  onDone: () => void;
  onError: (e: unknown) => void;
}) {
  const [form, setForm] = useState({
    dayOfWeek: '1',
    periodId: '',
    subjectId: '',
    staffId: '',
    room: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const add = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/timetable-entries`, { method: 'POST', body }),
    onSuccess: () => {
      onDone();
      setForm((f) => ({ ...f, room: '' }));
    },
    onError,
  });
  const select = (id: string, label: string, key: keyof typeof form, children: ReactNode) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      <select
        id={id}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className={selectClass}
      >
        {children}
      </select>
      {errors[key] && (
        <p role="alert" className="text-xs text-red-700">
          {errors[key]}
        </p>
      )}
    </div>
  );
  return (
    <form
      noValidate
      aria-label={t('tt.lesson')}
      onSubmit={(e) => {
        e.preventDefault();
        const parsed = createTimetableEntrySchema.safeParse({
          sectionId,
          dayOfWeek: Number(form.dayOfWeek),
          periodId: form.periodId,
          subjectId: form.subjectId,
          staffId: form.staffId,
          room: form.room || null,
        });
        if (!parsed.success) return setErrors(issuesToErrors(parsed.error.issues));
        setErrors({});
        add.mutate(parsed.data);
      }}
      className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3 lg:grid-cols-6"
    >
      {select(
        'lesson-day',
        t('tt.day'),
        'dayOfWeek',
        [1, 2, 3, 4, 5, 6, 0].map((d) => (
          <option key={d} value={d}>
            {t(`tt.day.${d}` as MessageKey)}
          </option>
        )),
      )}
      {select(
        'lesson-period',
        t('tt.period'),
        'periodId',
        <>
          <option value="">{t('tt.choose')}</option>
          {periods.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.startTime})
            </option>
          ))}
        </>,
      )}
      {select(
        'lesson-subject',
        t('tt.subject'),
        'subjectId',
        <>
          <option value="">{t('tt.choose')}</option>
          {subjects.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </>,
      )}
      {select(
        'lesson-teacher',
        t('tt.teacher'),
        'staffId',
        <>
          <option value="">{t('tt.choose')}</option>
          {staff.map((s) => (
            <option key={s.id} value={s.id}>
              {s.firstName} {s.lastName}
            </option>
          ))}
        </>,
      )}
      <TextField
        label={t('tt.room')}
        value={form.room}
        error={errors.room}
        onChange={(e) => setForm({ ...form, room: e.target.value })}
      />
      <div className="self-end">
        <button type="submit" className={primaryButton} disabled={add.isPending}>
          {t('tt.addLesson')}
        </button>
      </div>
      <HiddenFieldErrors
        errors={errors}
        visible={['dayOfWeek', 'periodId', 'subjectId', 'staffId', 'room']}
      />
    </form>
  );
}

function PeriodsPanel({
  schoolId,
  periods,
  onDone,
  onError,
  onDelete,
}: {
  schoolId: string;
  periods: Period[];
  onDone: () => void;
  onError: (e: unknown) => void;
  onDelete: (id: string) => void;
}) {
  const [form, setForm] = useState({ name: '', startTime: '', endTime: '', isBreak: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const add = useMutation({
    mutationFn: (body: object) => api(`/schools/${schoolId}/periods`, { method: 'POST', body }),
    onSuccess: () => {
      setForm({ name: '', startTime: '', endTime: '', isBreak: false });
      onDone();
    },
    onError,
  });
  return (
    <section aria-label={t('tt.periods')} className="space-y-3">
      <h2 className="text-lg font-semibold">{t('tt.periods')}</h2>
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {periods.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-2 p-3">
            <span>
              <span className="font-medium">{p.name}</span> {p.startTime}–{p.endTime}
              {p.isBreak && (
                <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-xs">
                  {t('tt.isBreak')}
                </span>
              )}
            </span>
            <button
              type="button"
              className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
              aria-label={`${t('tt.deletePeriod')}: ${p.name}`}
              onClick={() =>
                window.confirm(t('common.confirmDelete', { name: p.name })) && onDelete(p.id)
              }
            >
              {t('tt.deletePeriod')}
            </button>
          </li>
        ))}
      </ul>
      <form
        noValidate
        aria-label={t('tt.addPeriod')}
        onSubmit={(e) => {
          e.preventDefault();
          const parsed = createPeriodSchema.safeParse(form);
          if (!parsed.success) return setErrors(issuesToErrors(parsed.error.issues));
          setErrors({});
          add.mutate(parsed.data);
        }}
        className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-5"
      >
        <TextField
          label={t('tt.periodName')}
          value={form.name}
          error={errors.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <TextField
          label={t('tt.start')}
          type="time"
          value={form.startTime}
          error={errors.startTime}
          onChange={(e) => setForm({ ...form, startTime: e.target.value })}
        />
        <TextField
          label={t('tt.end')}
          type="time"
          value={form.endTime}
          error={errors.endTime}
          onChange={(e) => setForm({ ...form, endTime: e.target.value })}
        />
        <label className="flex items-center gap-2 self-end text-sm">
          <input
            type="checkbox"
            checked={form.isBreak}
            onChange={(e) => setForm({ ...form, isBreak: e.target.checked })}
          />
          {t('tt.isBreak')}
        </label>
        <div className="self-end">
          <button type="submit" className={primaryButton} disabled={add.isPending}>
            {t('tt.addPeriod')}
          </button>
        </div>
        <HiddenFieldErrors errors={errors} visible={['name', 'startTime', 'endTime']} />
      </form>
    </section>
  );
}
